import { CommonModule } from '@angular/common';
import { StandardIds } from '@galaxy/crm/services/field-service';
import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  Signal,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  FieldValueInterface,
  FormInputComponent,
  InlineEditService,
  ObjectType,
  SimplifiedUser,
} from '@galaxy/crm/static';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import {
  BehaviorSubject,
  Observable,
  of,
} from 'rxjs';
import {
  CRMUserService,
  userFilterFn,
} from '../../user.service';
import {
  tap,
  map,
  take,
  catchError,
  switchMap,
} from 'rxjs/operators';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';

const NO_VALUE_FOR_TEAM_MEMBER = '<NO_VALUE_FOR_TEAM_MEMBER>';

@Component({
  templateUrl: './team-member-form-input.component.html',
  providers: [],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GalaxyFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    MatIcon,
    MatIconButton,
  ],
  styleUrls: ['../shared.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TeamMemberFormInputComponent extends FormInputComponent {
  NO_VALUE_FOR_TEAM_MEMBER = NO_VALUE_FOR_TEAM_MEMBER;

  readOnly = false;
  objectType: ObjectType = 'Contact';
  _control: FormControl<SimplifiedUser | string> = new FormControl<SimplifiedUser | string>('', {
    nonNullable: true,
  });
  _label = '';
  searchTerm = signal<SimplifiedUser | string>('');
  private _group: UntypedFormGroup;

  private getAdditionalTeamMemberIDsFieldId(): string {
    if (this.objectType === 'Contact') {
      return StandardIds.ContactAdditionalTeamMemberIDs;
    } else {
      return StandardIds.CompanyAdditionalTeamMemberIDs;
    }
  }

  set control(control: FormControl<SimplifiedUser | string>) {
    this._control = control;
    if (typeof this._control.value === 'string') {
      this.userService
        .getMultiUsers([this._control.value])
        .pipe(
          take(1),
          catchError(() => {
            return of(['']);
          }),
        )
        .subscribe((susr) => {
          this._control.setValue(susr[0]);
          this.searchTerm.set(susr[0]);
        });
    } else {
      this._control.setValue(this._control.value || '');
      this.searchTerm.set('');
    }
  }

  get control(): UntypedFormControl {
    return this._control;
  }

  set formGroup(group: UntypedFormGroup) {
    this._group = group;
    group.addValidators((control) => {
      const path = this.getAdditionalTeamMemberIDsFieldId();
      const teamMemberControl = control.get(this._fieldId());
      const additionalTeamMemberControl = control.get(path);
      if (teamMemberControl && additionalTeamMemberControl) {
        const additionalTeamMemberValue: string[] = additionalTeamMemberControl.value;
        const teamMemberValue: string | SimplifiedUser = teamMemberControl.value;
        const userID = typeof teamMemberValue === 'string' ? teamMemberValue : teamMemberValue?.userId;
        if (teamMemberValue && additionalTeamMemberValue) {
          if (additionalTeamMemberValue.includes(userID)) {
            return {
              ownerIsInAdditionalTeamMembers: true,
            };
          }
        }
      }
      return null;
    });
  }

  get formGroup(): UntypedFormGroup {
    return this._group;
  }

  _fieldId = signal('');
  set fieldId(fieldId: string) {
    this._fieldId.set(fieldId);
  }

  get hasModification(): boolean {
    return this._control.dirty;
  }

  set label(label: string) {
    this._label = label;
  }

  get label(): string {
    return this._label;
  }

  get value(): FieldValueInterface {
    const controlValue: SimplifiedUser | string = this._control.value;
    if (typeof controlValue === 'string') {
      return {
        fieldId: this._fieldId(),
        stringValue: controlValue,
      };
    }
    return {
      fieldId: this._fieldId(),
      stringValue: controlValue?.userId,
    };
  }

  loadingValues = signal(true);
  availableOptions = signal<SimplifiedUser[]>([]);
  filteredOptions: Signal<SimplifiedUser[]> = computed(() => {
    const searchTerm = this.searchTerm();
    const filterTerm = typeof searchTerm === 'string' ? searchTerm : searchTerm?.userId;
    const availableOptions = this.availableOptions();
    return availableOptions.filter((tm) => userFilterFn(tm, filterTerm));
  });
  teamMemberName = computed(() => {
    const searchTerm = this.searchTerm();
    const availableOptions = this.availableOptions();
    const filterTerm = typeof searchTerm === 'string' ? searchTerm : searchTerm?.userId;
    if (!searchTerm) {
      return NO_VALUE_FOR_TEAM_MEMBER;
    }
    const found = availableOptions.find((tm) => userFilterFn(tm, filterTerm));
    return found?.displayName || searchTerm;
  });

  @ViewChild('input') input: ElementRef<HTMLInputElement>;
  input$$ = new BehaviorSubject<string>('');

  constructor(
    private readonly userService: CRMUserService,
    private readonly destroyRef: DestroyRef,
    private readonly inlineEditService: InlineEditService,
  ) {
    super();
    this.searchUsers('').subscribe((allTeamMembers) => {
      this.availableOptions.set(allTeamMembers);
      this.loadingValues.set(false);
    });

    this.input$$
      .asObservable()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((searchValue) =>
          this.searchUsers(searchValue).pipe(
            map((results) => {
              return { searchValue, results };
            }),
          ),
        ),
      )
      .subscribe((newValue) => {
        this.availableOptions.set(newValue.results);
        this.searchTerm.set(newValue.searchValue);
      });
  }

  clear(): void {
    this._control.reset();
    this.searchTerm.set('');
  }

  searchUsers(searchTerm): Observable<SimplifiedUser[]> {
    const filterTerm = typeof searchTerm === 'string' ? searchTerm : searchTerm?.userId;

    let teamMemberID: string;
    if (this._control.value && typeof this._control.value === 'object') {
      teamMemberID = this._control.value.userId;
    } else if (typeof this._control.value === 'string') {
      teamMemberID = this._control.value;
    }

    return this.userService.searchUsers(filterTerm as string).pipe(
      tap((allTeamMembers) => {
        if (teamMemberID) {
          const found = allTeamMembers.find((tm) => tm.userId === teamMemberID);
          if (!found) {
            this._control.markAsDirty();
          }
        }
      }),
    );
  }

  displayFn(availableOptions: SimplifiedUser[]): (teamMember: SimplifiedUser | string) => string {
    return (teamMember: SimplifiedUser | string): string => {
      if (!teamMember) {
        return '';
      }
      if (typeof teamMember === 'string') {
        const found = availableOptions.find((tm) => tm.userId === teamMember);
        return found?.displayName || teamMember;
      }
      return teamMember?.displayName;
    };
  }

  filter(): void {
    this.input$$.next(this.input.nativeElement.value);
  }

  saveField(): void {
    if (!this._control.errors && !this._group.errors?.ownerIsInAdditionalTeamMembers) {
      // attempt saving additional team members field as well, if there are no changes it won't save
      this.inlineEditService.saveInlineEdit(this.objectType, [
        this.value,
        {
          fieldId: this.getAdditionalTeamMemberIDsFieldId(),
          stringValues: {
            values: this._group?.value[this.getAdditionalTeamMemberIDsFieldId()],
          },
        },
      ]);
    }
  }
}
